import { Copy } from "lucide-react";
import { useEffect, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaSurface } from "@/shared/components/layout/ColocaSurface";
import useTranslations from "@/shared/lang/useTranslations";
import { colocaToast } from "@/shared/utils/toast";

import { formatPayinRemaining, usePayinCountdown } from "../hooks/usePayinCountdown";
import { pixQrDataUrl } from "../utils/pixQrDataUrl";
import { PayinQrCard } from "./PayinQrCard";

export interface PayinQrPanelProps {
  /** The runtime's EMV payload. The QR is rendered from it client-side. */
  payload: string;
  /** ISO expiry of the collection instrument, or null when the rail sets none. */
  expiresAt: string | null;
  /** Rail of the payin, for the image's alt text. */
  rail: string;
  /** Formatted amount with its currency, printed on the shareable image. */
  amount?: string;
}

/**
 * The collection QR of a payin that has not been paid yet, as the activity drawer re-shows it: the rendered
 * code, how long it is still good for and, on PIX only, the copy-and-paste payload.
 *
 * The countdown is the point of the panel, not decoration: the instrument is perishable, and a bare timestamp
 * makes the reader do arithmetic to answer the only question they have — is this still worth showing to a
 * payer. When it reaches zero the panel says so instead of vanishing; the runtime stops serving the payload
 * shortly after (the read gate is `CREATED` and unexpired), which is what actually removes the panel.
 */
export function PayinQrPanel({ payload, expiresAt, rail, amount }: PayinQrPanelProps) {
  const { t, activeLanguage } = useTranslations("payins");
  const [qrSrc, setQrSrc] = useState<string | null>(null);
  const remaining = usePayinCountdown(expiresAt);
  const isExpired = remaining !== null && remaining <= 0;

  // The runtime returns the EMV string, not an image; the card shows its skeleton until it is drawn.
  useEffect(() => {
    let cancelled = false;
    void pixQrDataUrl(payload).then((url) => {
      if (!cancelled) setQrSrc(url);
    });
    return () => {
      cancelled = true;
    };
  }, [payload]);

  const copyPayload = async () => {
    try {
      await navigator.clipboard.writeText(payload);
      colocaToast.success(t("qr_panel_copied"));
    } catch {
      // Clipboard denied (permissions, insecure context): the payload is on screen to select by hand.
    }
  };

  if (isExpired) {
    return (
      <ColocaSurface variant="muted" padding="md">
        <p className="text-xs text-muted-foreground">{t("qr_panel_expired")}</p>
      </ColocaSurface>
    );
  }

  return (
    <ColocaSurface padding="md">
      <PayinQrCard
        src={qrSrc}
        rail={rail}
        footer={
          remaining !== null ? t("qr_panel_expires_in", { time: formatPayinRemaining(remaining) }) : undefined
        }
        amount={amount}
        shareExpiry={
          expiresAt
            ? `${t("breb_success_expires")} ${new Date(expiresAt).toLocaleString(activeLanguage, {
                dateStyle: "medium",
                timeStyle: "short",
              })}`
            : undefined
        }
      />

      {/* No Colombian banking app accepts a pasted Bre-B code; PIX's copia e cola does. */}
      {rail !== "BREB" && (
        <div className="mt-4 border-t border-border/60 pt-4">
          <p className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
            {t("qr_panel_copy_label")}
          </p>
          <p className="mt-2 break-all font-mono text-xs leading-relaxed text-foreground">{payload}</p>
          <ColocaButton
            variant="secondary"
            size="sm"
            icon={Copy}
            className="mt-3"
            onClick={() => void copyPayload()}
          >
            {t("qr_panel_copy_button")}
          </ColocaButton>
        </div>
      )}
    </ColocaSurface>
  );
}
